import type { GatewayBrowserClient } from "../gateway.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import type {
  SessionArtifactFileSummary,
  SessionArtifactSummary,
  SessionArtifactSummaryTotals,
} from "./chat-file-changes-summary.ts";
import {
  fetchArtifactSummaryForPending,
  type FileChangesNoticeHost,
} from "./chat-file-changes-notice.ts";

export type ChatLastRunSnapshot = {
  files: SessionArtifactFileSummary[];
  totals: SessionArtifactSummaryTotals;
};

export type ChatLastRunFilesHost = {
  sessionKey: string;
  chatLastRunBySession: Record<string, ChatLastRunSnapshot>;
};

export type ChatLastRunRefreshHost = ChatLastRunFilesHost &
  FileChangesNoticeHost & {
    chatSubagents: ChatSubagentCard[];
    chatMessages?: unknown[];
    client: GatewayBrowserClient | null;
    connected?: boolean;
  };

export function emptyLastRunTotals(): SessionArtifactSummaryTotals {
  return {
    files: 0,
    created: 0,
    modified: 0,
    deleted: 0,
    additions: 0,
    deletions: 0,
  };
}

export function totalsFromFiles(files: SessionArtifactFileSummary[]): SessionArtifactSummaryTotals {
  const totals = emptyLastRunTotals();
  totals.files = files.length;
  for (const f of files) {
    if (f.op === "created") {
      totals.created += 1;
    } else if (f.op === "deleted") {
      totals.deleted += 1;
    } else {
      totals.modified += 1;
    }
    totals.additions += f.additions;
    totals.deletions += f.deletions;
  }
  return totals;
}

export function resolveChatLastRunSnapshot(
  host: ChatLastRunFilesHost,
  sessionKey: string,
): ChatLastRunSnapshot | null {
  const key = sessionKey.trim();
  if (!key) {
    return null;
  }
  return host.chatLastRunBySession[key] ?? null;
}

export function setChatLastRunSnapshot(
  host: ChatLastRunFilesHost,
  sessionKey: string,
  summary: SessionArtifactSummary,
): void {
  const key = sessionKey.trim();
  if (!key || !summary.files.length) {
    return;
  }
  host.chatLastRunBySession = {
    ...host.chatLastRunBySession,
    [key]: {
      files: summary.files,
      totals: summary.totals ?? totalsFromFiles(summary.files),
    },
  };
}

export function clearChatLastRunSnapshot(host: ChatLastRunFilesHost, sessionKey: string): void {
  const key = sessionKey.trim();
  const bySession = host.chatLastRunBySession;
  if (!key || !bySession?.[key]) {
    return;
  }
  const next = { ...bySession };
  delete next[key];
  host.chatLastRunBySession = next;
}

export async function refreshChatLastRunFiles(host: ChatLastRunRefreshHost): Promise<void> {
  const sessionKey = host.sessionKey.trim();
  if (!sessionKey || !host.client || !host.connected) {
    return;
  }
  const summary = await fetchArtifactSummaryForPending(host);
  if (summary?.files?.length) {
    setChatLastRunSnapshot(host, sessionKey, summary);
  }
}
